// src/components/character/CharacterSheetPrintPage.jsx
//
// Standalone route (/character/:slug/print) for the printable character
// sheet. Unlike the old preview (a hidden window filled in via
// document.write, with no URL of its own), this is a normal app page: it
// fetches its own data from the character's slug, so refresh, and browser
// back/forward, all just work like any other route.
import React, { useEffect, useMemo, useState } from "react";
import PrintableCharacterSheet from "./PrintableCharacterSheet";
import { buildSheetQrDataUrl } from "./printCharacterSheet";
import { decodeCharacterId } from "./characterSlug";
import { updateCalculatedFields } from "./characterCalculations";
import { addStaticSkills } from "./utils";
import * as api from "../../utils/api";

const GOOGLE_FONTS_HREF =
    "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=Cinzel+Decorative:wght@400;700&display=swap";
const PRINT_CSS_HREF = `${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/print/character-sheet.print.css`;

/** Decode {playerId} out of the JWT payload (same parsing App.js does on load). */
function playerIdFromToken(token) {
    if (!token) return null;
    try {
        const [, payload] = token.split(".");
        return JSON.parse(atob(payload))?.playerId ?? null;
    } catch {
        return null;
    }
}

/**
 * The print stylesheet (and the heavier Cinzel weights) only belong on this
 * page — loading them via <link> tags appended to <head> keeps them out of
 * the rest of the SPA, and the cleanup on unmount undoes it when the user
 * navigates away client-side (no full page reload to reset the document).
 */
function usePrintAssets() {
    useEffect(() => {
        const links = [
            { rel: "preconnect", href: "https://fonts.googleapis.com" },
            { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
            { rel: "stylesheet", href: GOOGLE_FONTS_HREF },
            { rel: "stylesheet", href: PRINT_CSS_HREF },
        ];
        const elements = links.map(({ rel, href, crossOrigin }) => {
            const el = document.createElement("link");
            el.rel = rel;
            el.href = href;
            if (crossOrigin) el.crossOrigin = crossOrigin;
            document.head.appendChild(el);
            return el;
        });
        return () => elements.forEach((el) => el.remove());
    }, []);
}

export default function CharacterSheetPrintPage({ token, slug }) {
    usePrintAssets();

    const characterId = useMemo(() => decodeCharacterId(slug), [slug]);
    const playerId = useMemo(() => playerIdFromToken(token), [token]);

    const [sheetData, setSheetData] = useState(null); // { character, races, skillsets, availableSkills }
    const [loadError, setLoadError] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);

    useEffect(() => {
        api.setToken(token);
    }, [token]);

    useEffect(() => {
        if (characterId == null) return;
        let cancelled = false;
        (async () => {
            try {
                const [character, races, skillsets, availableSkills] = await Promise.all([
                    api.fetchCharacter(characterId),
                    api.fetchRaces(),
                    api.fetchSkillsets(),
                    api.fetchSkills({ characterId }),
                ]);

                if (character.transformId) {
                    try {
                        const t = await api.fetchTransform(character.transformId);
                        character.transformName = t?.name;
                    } catch { /* transform lookup is best-effort */ }
                }
                if (character.playerId) {
                    try {
                        const p = await api.fetchPlayer(character.playerId);
                        character.playerName = p?.name || "Guest";
                    } catch { /* player lookup is best-effort */ }
                }

                // The character API doesn't return per-skill SP cost or the
                // attribute/armor pools (Body/Armor Cap/Dex Armor/Agility/etc.)
                // directly — CharacterSkills.js derives them client-side from
                // the character's purchased skills, and the interactive editor
                // only shows correct numbers because that derivation has
                // already run by the time you'd print/preview from there. This
                // page fetches independently, so it has to run the same
                // derivation itself.
                const enrichedCharacter = updateCalculatedFields(
                    { ...character, skills: addStaticSkills(character.skills, availableSkills) },
                    availableSkills
                );

                if (!cancelled) setSheetData({ character: enrichedCharacter, races, skillsets, availableSkills });
            } catch {
                if (!cancelled) setLoadError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [characterId]);

    // Resolve a QR code for the character's current build on every load
    // (including refresh). The server reuses an existing qrCodeId when the
    // build is unchanged (see POST /qrCode), so this doesn't mint a new row
    // each time — only when the build has actually changed.
    useEffect(() => {
        if (!sheetData) return;
        let cancelled = false;
        (async () => {
            const dataUrl = await buildSheetQrDataUrl(sheetData.character, { printedByPlayerId: playerId });
            if (!cancelled) setQrDataUrl(dataUrl);
        })();
        return () => { cancelled = true; };
    }, [sheetData, playerId]);

    useEffect(() => {
        document.title = sheetData?.character?.name
            ? `${sheetData.character.name} — Character Sheet`
            : "Character Sheet";
        return () => { document.title = "Chronicles"; };
    }, [sheetData?.character?.name]);

    if (characterId == null || loadError) {
        return (
            <div style={{ padding: "2rem", fontFamily: "sans-serif" }}>
                Character not found, or you don't have access to it.
            </div>
        );
    }

    if (!sheetData) {
        return <div style={{ padding: "2rem", fontFamily: "sans-serif" }}>Loading character sheet…</div>;
    }

    return (
        <PrintableCharacterSheet
            character={sheetData.character}
            races={sheetData.races}
            skillsets={sheetData.skillsets}
            availableSkills={sheetData.availableSkills}
            qrDataUrl={qrDataUrl}
        />
    );
}
